import type { GiftContentType, GiftTrackingDto } from '@/api/types/gift';
import type { ProductDetailGiftEntrance, ProductDetailGiftPayload } from '@/api/types/product';

export interface ProductDetailGiftTermsViewModel {
  show?: boolean;
  label?: string;
  content?: string;
  contentType?: GiftContentType;
}

export interface ProductDetailGiftDialogTierViewModel {
  id: string | number;
  name: string;
  thresholdText?: string;
  thumbnail: string;
  largeImage?: string;
  soldOut?: boolean;
}

export interface ProductDetailGiftDialogActivityViewModel {
  id: string | number;
  title: string;
  ruleLabel?: string;
  terms?: ProductDetailGiftTermsViewModel;
  tiers: ProductDetailGiftDialogTierViewModel[];
  eventPeriod?: string;
  descriptionLines?: string[];
  displayStyle?: 'single-tier' | 'multi-tier';
  activityType?: 'minimumPurchase' | 'purchase';
  singleGift?: ProductDetailGiftPurchaseGiftViewModel;
  purchaseGiftTitle?: string;
  purchaseGift?: ProductDetailGiftPurchaseGiftViewModel;
}

export interface ProductDetailGiftSingleDialogViewModel {
  id: string | number;
  name: string;
  color?: string;
  images: string[];
  isGlass?: boolean;
  sellPrice?: string | number;
  oldPrice?: string | number;
  stockText?: string;
  comeWithText?: string;
  eventPeriod?: string;
  rules?: string[];
}

export interface ProductDetailGiftPurchaseGiftViewModel {
  id: string | number;
  name: string;
  image: string;
  isGlass?: boolean;
  subtitle?: string;
  thresholdText?: string;
  detail: ProductDetailGiftSingleDialogViewModel;
}

export interface ProductDetailGiftTeaserViewModel {
  title: string;
  image: string;
  images: string[];
  isGlass?: boolean;
  ruleText?: string;
  color?: string;
  sellPrice?: string;
  oldPrice?: string;
  stockText?: string;
  eventPeriod?: string;
  rules: string[];
  terms?: ProductDetailGiftTermsViewModel;
  tracking?: GiftTrackingDto;
}

export interface ProductDetailGiftViewModel {
  teaser: ProductDetailGiftTeaserViewModel;
  useDialogFlow: boolean;
  dialogActivities: ProductDetailGiftDialogActivityViewModel[];
}

type ProductDetailsPageButtonProperties = Record<string, unknown>;
type ProductDetailGiftInteractionReporter = (
  buttonName: string,
  properties?: ProductDetailsPageButtonProperties,
) => void;

export const PDP_GIFT_INTERACTION_NAMES = {
  entrance: 'Free Gift',
  rule: 'Free Gift Rule',
  tier: 'Free Gift Tier',
  image: 'Free Gift Image',
} as const;

export function reportProductDetailGiftInteraction(
  reporter: ProductDetailGiftInteractionReporter | undefined,
  interactionName: (typeof PDP_GIFT_INTERACTION_NAMES)[keyof typeof PDP_GIFT_INTERACTION_NAMES],
  tracking?: GiftTrackingDto,
) {
  if (!reporter) return;
  if (tracking) {
    reporter(interactionName, tracking);
    return;
  }
  reporter(interactionName);
}

export function mergeProductDetailsPageButtonProperties(
  baseProperties: ProductDetailsPageButtonProperties,
  additionalProperties?: ProductDetailsPageButtonProperties,
) {
  return {
    ...additionalProperties,
    ...baseProperties,
  };
}

const getNonEmptyString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const getStringOrNumber = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

const getImages = (images?: string[]) =>
  (images || []).map(getNonEmptyString).filter((image): image is string => Boolean(image));

const isGlass = (value: unknown) => value === 1 || value === '1';

const formatEventPeriod = (startTime?: string | number, endTime?: string | number) => {
  if (!startTime || !endTime) return undefined;
  return `${startTime} - ${endTime}`;
};

const splitDescription = (description?: string) =>
  description
    ?.split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean) || [];

const getTierImage = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) =>
  getNonEmptyString(tier.thumbnail) ||
  getNonEmptyString(tier.gift_thumbnail_image) ||
  getNonEmptyString(tier.large_image) ||
  getNonEmptyString(tier.gift_large_image);

const getTierLargeImage = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) =>
  getNonEmptyString(tier.large_image) ||
  getNonEmptyString(tier.gift_large_image) ||
  getNonEmptyString(tier.thumbnail) ||
  getNonEmptyString(tier.gift_thumbnail_image);

const getTierName = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) => getNonEmptyString(tier.name) || getNonEmptyString(tier.gift_name);

const getTerms = (
  termsEntry?: ProductDetailGiftEntrance['terms_entry'],
  fallback?: { content?: string; contentType?: GiftContentType; label?: string },
): ProductDetailGiftTermsViewModel | undefined => {
  if (!termsEntry && !fallback?.content) return undefined;

  const content = termsEntry?.content || fallback?.content;
  return {
    show: termsEntry?.show ?? Boolean(content),
    label: termsEntry?.label || fallback?.label,
    content,
    contentType: termsEntry?.content_type || fallback?.contentType,
  };
};

const getTierDisclaimerFallback = (
  tiers: NonNullable<NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']>,
) => {
  const tier =
    tiers.find((item) => item.is_current === true && getNonEmptyString(item.disclaimer)) ||
    tiers.find((item) => getNonEmptyString(item.disclaimer));
  if (!tier) return undefined;

  return {
    content: getNonEmptyString(tier.disclaimer),
    contentType: tier.disclaimer_content_type,
  };
};

const getPurchaseGift = (
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  activityIndex: number,
  images: string[],
): ProductDetailGiftPurchaseGiftViewModel | undefined => {
  const source = activity.purchase_gift;
  if (!source || typeof source !== 'object') return undefined;

  const image = getNonEmptyString(source.image);
  const name = getNonEmptyString(source.name);
  if (!image || !name) return undefined;

  const id =
    getStringOrNumber(source.id) ?? `${activity.id ?? activity.activity_id ?? activityIndex}-gift`;
  const activityImages = getImages(activity.images);
  const resolvedImages = activityImages.length > 0 ? activityImages : images;
  const eventPeriod =
    getNonEmptyString(source.event_period) ||
    formatEventPeriod(activity.start_time, activity.end_time);
  const rules = Array.isArray(source.rules)
    ? source.rules.map(getNonEmptyString).filter((rule): rule is string => Boolean(rule))
    : undefined;

  return {
    id,
    name,
    image,
    isGlass: isGlass(source.is_glass),
    subtitle: getNonEmptyString(source.subtitle),
    detail: {
      id,
      name,
      color: getNonEmptyString(source.color),
      images: resolvedImages.length > 0 ? resolvedImages : [image],
      isGlass: isGlass(source.is_glass),
      sellPrice: getStringOrNumber(source.sell_price),
      oldPrice: getStringOrNumber(source.old_price),
      stockText: getNonEmptyString(source.stock_text),
      comeWithText: getNonEmptyString(source.come_with_text),
      eventPeriod,
      rules,
    },
  };
};

const getActivityType = (
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
) => {
  if (activity.activity_type === 4 || activity.activity_type === 5) {
    return 'minimumPurchase' as const;
  }

  if (activity.activity_type === 2 || activity.activity_type === 3) {
    return 'purchase' as const;
  }

  if (
    activity.activity_type_key === 'full_gift' ||
    activity.activity_type_key === 'full_stair_gift'
  ) {
    return 'minimumPurchase' as const;
  }

  if (
    activity.activity_type_key === 'buy_gift' ||
    activity.activity_type_key === 'buy_stair_gift'
  ) {
    return 'purchase' as const;
  }

  return undefined;
};

const getSingleTierGift = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  activityIndex: number,
): ProductDetailGiftPurchaseGiftViewModel | undefined => {
  const image = getTierImage(tier);
  const name = getTierName(tier);
  if (!image || !name) return undefined;

  const id =
    getStringOrNumber(tier.id) ??
    getStringOrNumber(tier.tier_id) ??
    getStringOrNumber(tier.stair_index) ??
    `${activity.id ?? activity.activity_id ?? activityIndex}-tier`;
  const largeImage = getTierLargeImage(tier) || image;

  return {
    id,
    name,
    image,
    thresholdText: getNonEmptyString(tier.threshold_text),
    detail: {
      id,
      name,
      images: [largeImage],
    },
  };
};

const mapActivity = (
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  activityIndex: number,
  detailImages: string[],
): ProductDetailGiftDialogActivityViewModel => {
  const eventPeriod = formatEventPeriod(activity.start_time, activity.end_time);
  const descriptionLines = splitDescription(activity.description);

  const tiers = (activity.tiers || []).map((tier, tierIndex) => ({
    id: tier.id ?? tier.tier_id ?? tier.stair_index ?? `${activityIndex}-${tierIndex}`,
    name: getTierName(tier) || '',
    thresholdText: tier.threshold_text,
    thumbnail: getTierImage(tier) || '',
    largeImage: getTierLargeImage(tier),
    soldOut: tier.is_sold_out ?? tier.sold_out,
  }));
  const tierTermsFallback = getTierDisclaimerFallback(activity.tiers || []);

  const purchaseGift = getPurchaseGift(activity, activityIndex, detailImages);
  const singleGift =
    tiers.length === 1
      ? getSingleTierGift(activity.tiers?.[0], activity, activityIndex)
      : tiers.length === 0
        ? purchaseGift
          ? { ...purchaseGift, thresholdText: activity.rule_label }
          : undefined
        : undefined;
  const displayStyle = singleGift ? 'single-tier' : tiers.length > 1 ? 'multi-tier' : undefined;

  return {
    id: activity.id ?? activity.activity_id ?? activityIndex,
    title: activity.title || '',
    ruleLabel: activity.rule_label,
    terms: getTerms(activity.terms_entry, {
      // 后端约定：活动级免责声明优先，阶梯级免责声明仅作为兜底。
      content: activity.disclaimer_content || tierTermsFallback?.content,
      contentType: activity.disclaimer_content_type || tierTermsFallback?.contentType,
      label: activity.rule_label,
    }),
    eventPeriod,
    descriptionLines: descriptionLines.length > 0 ? descriptionLines : undefined,
    tiers,
    ...(displayStyle
      ? {
          displayStyle,
          activityType: getActivityType(activity),
          ...(singleGift ? { singleGift } : {}),
        }
      : {}),
    ...(!displayStyle && purchaseGift
      ? {
          purchaseGiftTitle: getNonEmptyString(activity.purchase_gift_title),
          purchaseGift,
        }
      : {}),
  };
};

export function buildProductDetailGiftViewModel(
  payload?: ProductDetailGiftPayload | null,
): ProductDetailGiftViewModel | null {
  if (payload?.show !== true || !payload.entrance) return null;

  const entrance = payload.entrance;
  const entranceImage = getNonEmptyString(entrance?.gift_image);
  const title = getNonEmptyString(entrance.gift_name) || getNonEmptyString(entrance.title);

  if (!title || !entranceImage) return null;

  const entranceTerms = getTerms(entrance.terms_entry);
  const detailImages = getImages(payload.images);
  const detailActivities = (payload.detail_activities || []).map((activity, index) =>
    mapActivity(activity, index, detailImages),
  );

  return {
    teaser: {
      title,
      image: entranceImage,
      images: [entranceImage],
      isGlass: isGlass(entrance.is_glass),
      ruleText: getNonEmptyString(entrance.threshold_text),
      rules: [],
      terms: entranceTerms,
      tracking: entrance.tracking,
    },
    useDialogFlow: detailActivities.length > 0,
    dialogActivities: detailActivities,
  };
}
